import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell,
} from "recharts";
import { ArrowUpRight, AlertTriangle, CheckCircle2, Clock, ShieldCheck, TrendingUp } from "lucide-react";

const STATUS_COLORS = {
  pending: "hsl(220 10% 55%)",
  assigned: "hsl(222 44% 30%)",
  in_progress: "hsl(38 92% 50%)",
  completed: "hsl(158 74% 40%)",
  verified: "hsl(158 74% 40%)",
  rejected: "hsl(0 78% 55%)",
  overdue: "hsl(0 78% 55%)",
  cancelled: "hsl(220 10% 45%)",
};

function KpiCard({ label, value, hint, tone = "default", testId, icon: Icon }) {
  const toneStyle = {
    default: { bg: "hsl(220 15% 96%)", fg: "hsl(220 30% 30%)" },
    success: { bg: "hsl(158 74% 92%)", fg: "hsl(158 74% 30%)" },
    warning: { bg: "hsl(38 92% 90%)", fg: "hsl(38 92% 35%)" },
    destructive: { bg: "hsl(0 78% 94%)", fg: "hsl(0 78% 45%)" },
  }[tone];
  return (
    <div
      className="border border-border bg-card rounded-2xl p-5 relative group hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200"
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-3">
        <span className="text-[0.7rem] tracking-widest uppercase font-semibold text-muted-foreground">{label}</span>
        {Icon && (
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: toneStyle.bg, color: toneStyle.fg }}
          >
            <Icon className="w-4 h-4" />
          </div>
        )}
      </div>
      <div className="font-display font-black text-3xl md:text-4xl tracking-tight" data-testid={`${testId}-value`}>{value}</div>
      {hint && <div className="mt-2 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function TaskRow({ t }) {
  const color = STATUS_COLORS[t.status] || "hsl(220 10% 45%)";
  return (
    <Link
      to={`/app/tasks/${t.id}`}
      className="flex items-center justify-between py-3.5 px-5 hover:bg-accent/30 transition-colors group border-b border-border last:border-b-0"
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{t.title}</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          Due {new Date(t.due_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span
          className="text-[0.7rem] font-semibold capitalize px-2.5 py-1 rounded-full"
          style={{ background: `${color}18`, color }}
        >
          {t.status.replace("_", " ")}
        </span>
        <ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();

  const { data: stats } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/reports/dashboard")).data,
    refetchInterval: 15000,
  });

  const { data: tasks } = useQuery({
    queryKey: ["dashboard-tasks"],
    queryFn: async () => (await api.get("/tasks")).data,
    refetchInterval: 15000,
  });

  const { data: areaHealth } = useQuery({
    queryKey: ["area-health"],
    queryFn: async () => (await api.get("/reports/area-health")).data,
    enabled: user?.role !== "employee",
  });

  const recentTasks = (tasks || []).slice(0, 6);
  const upcoming = (tasks || []).filter(
    (t) => ["pending", "assigned", "in_progress"].includes(t.status)
  ).slice(0, 6);

  const statusData = Object.entries(stats?.status_counts || {}).map(([k, v]) => ({
    name: k.replace("_", " "),
    value: v,
    color: STATUS_COLORS[k],
  }));

  return (
    <div className="p-4 md:p-8 max-w-[1600px]">
      {/* Hero */}
      <div className="mb-8 bg-card border border-border rounded-2xl p-6 md:p-8 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[0.7rem] tracking-widest uppercase text-muted-foreground mb-2 font-semibold">Operations Overview</div>
            <h1 className="font-display text-2xl md:text-4xl font-black tracking-tight leading-tight">
              Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"},
              <br />
              <span className="text-primary">{user?.name?.split(" ")[0]}</span>.
            </h1>
            <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
              Live snapshot of your operational compliance across all facilities.
              Data refreshes automatically.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-xs text-muted-foreground bg-muted rounded-full px-3 py-1.5 font-medium">
              {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
            </div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard
          label="Compliance"
          value={`${stats?.compliance_pct ?? 0}%`}
          hint={`${stats?.verified ?? 0} of ${stats?.total_tasks ?? 0} tasks verified`}
          tone="success"
          testId="kpi-compliance"
          icon={ShieldCheck}
        />
        <KpiCard
          label="Overdue"
          value={stats?.overdue ?? 0}
          hint={stats?.overdue ? "Requires immediate attention" : "Clean slate"}
          tone={stats?.overdue ? "destructive" : "default"}
          testId="kpi-overdue"
          icon={AlertTriangle}
        />
        <KpiCard
          label="Completed"
          value={stats?.completed ?? 0}
          hint="Awaiting or approved"
          tone="default"
          testId="kpi-completed"
          icon={CheckCircle2}
        />
        <KpiCard
          label="Avg. Duration"
          value={`${stats?.avg_completion_minutes ?? 0}m`}
          hint="Start → Completion"
          tone="default"
          testId="kpi-avg-duration"
          icon={Clock}
        />
      </div>

      {/* Charts row */}
      <div className="grid lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 border border-border bg-card rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-caps text-muted-foreground">Completion Trend</div>
              <div className="font-display text-lg font-semibold mt-1">Last 14 days</div>
            </div>
            <TrendingUp className="w-4 h-4 text-primary" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats?.trend || []}>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(220 10% 60%)" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(220 10% 60%)" />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                />
                <Line type="monotone" dataKey="verified" stroke="hsl(158 84% 39%)" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="completed" stroke="hsl(227 100% 33%)" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="rejected" stroke="hsl(0 78% 45%)" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-4 mt-4 text-xs">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[hsl(158_84%_39%)]" />Verified</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-primary" />Completed</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-destructive" />Rejected</span>
          </div>
        </div>

        <div className="border border-border bg-card rounded-2xl p-6">
          <div className="label-caps text-muted-foreground">Task States</div>
          <div className="font-display text-lg font-semibold mt-1 mb-4">Distribution</div>
          <div className="h-52">
            {statusData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    dataKey="value"
                    innerRadius={45}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {statusData.map((s, i) => <Cell key={i} fill={s.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">No data yet</div>
            )}
          </div>
          <div className="space-y-1 mt-2">
            {statusData.slice(0, 4).map((s) => (
              <div key={s.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 capitalize">
                  <span className="w-2 h-2 rounded-sm" style={{ background: s.color }} />
                  {s.name}
                </span>
                <span className="font-mono font-medium">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Task lists row */}
      <div className="grid lg:grid-cols-2 gap-4 mb-8">
        <div className="border border-border bg-card rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div>
              <div className="label-caps text-muted-foreground">Upcoming</div>
              <div className="font-display text-lg font-semibold">Next tasks</div>
            </div>
            <Link to="/app/tasks" className="text-xs text-primary hover:underline font-medium">View all →</Link>
          </div>
          {upcoming.length ? upcoming.map((t) => <TaskRow key={t.id} t={t} />) : (
            <div className="p-6 text-sm text-muted-foreground text-center">Nothing scheduled.</div>
          )}
        </div>

        <div className="border border-border bg-card rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div>
              <div className="label-caps text-muted-foreground">Live Activity</div>
              <div className="font-display text-lg font-semibold">Recent tasks</div>
            </div>
            <Link to="/app/audit" className="text-xs text-primary hover:underline font-medium">Audit log →</Link>
          </div>
          {recentTasks.length ? recentTasks.map((t) => <TaskRow key={t.id} t={t} />) : (
            <div className="p-6 text-sm text-muted-foreground text-center">No activity yet.</div>
          )}
        </div>
      </div>

      {/* Productivity */}
      {stats?.productivity?.length ? (
        <div className="border border-border bg-card rounded-2xl p-6 mb-8">
          <div className="label-caps text-muted-foreground mb-1">Employee Productivity</div>
          <div className="font-display text-lg font-semibold mb-4">Top performers</div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.productivity} layout="vertical">
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} stroke="hsl(220 10% 60%)" />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Bar dataKey="verified" stackId="a" fill="hsl(158 84% 39%)" />
                <Bar dataKey="completed" stackId="a" fill="hsl(227 100% 33%)" />
                <Bar dataKey="rejected" stackId="a" fill="hsl(0 78% 45%)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : null}

      {/* Area Health */}
      {areaHealth?.length ? (
        <div className="border border-border bg-card rounded-2xl p-6">
          <div className="label-caps text-muted-foreground mb-1">Area Health</div>
          <div className="font-display text-lg font-semibold mb-4">Compliance by area (lowest first)</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {areaHealth.slice(0, 9).map((a) => (
              <div key={a.id} className="border border-border rounded-xl p-4 bg-background hover:border-primary/40 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{a.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{a.area_type || "Area"}</div>
                  </div>
                  <span
                    className="shrink-0 text-[0.7rem] font-semibold px-2.5 py-1 rounded-full"
                    style={{
                      background: a.overdue > 0
                        ? "hsl(0 78% 94%)"
                        : a.compliance_pct >= 80
                          ? "hsl(158 74% 92%)"
                          : "hsl(38 92% 90%)",
                      color: a.overdue > 0
                        ? "hsl(0 78% 45%)"
                        : a.compliance_pct >= 80
                          ? "hsl(158 74% 30%)"
                          : "hsl(38 92% 35%)",
                    }}
                  >
                    {a.compliance_pct}%
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2">
                  <span>{a.total} tasks</span>
                  {a.overdue > 0 && <span className="text-destructive font-medium">{a.overdue} overdue</span>}
                  {a.rejected > 0 && <span>{a.rejected} rejected</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
